// HQ Dashboard: KPIs, sparklines, pending decisions, this month's orders.

import type { ScreenProps } from "./shared.js";
import type { ScreenId } from "../App.js";
import { money, monthLabel, pct } from "../format.js";
import { Sparkline } from "../components/widgets.js";
import { CompanyIllustration } from "../components/CompanyIllustration.js";

export function Dashboard({ snap, content, session, go }: ScreenProps & { go: (s: ScreenId) => void }) {
  const { view, pending } = snap;
  const kpi = view.kpiHistory[view.kpiHistory.length - 1];
  const ttm = view.finance.ttmRevenue.reduce((a, b) => a + b, 0);
  const cashSeries = view.kpiHistory.slice(-48).map((k) => k.cash);
  const revSeries = view.kpiHistory.slice(-48).map((k) => k.revenue);
  const headSeries = view.kpiHistory.slice(-48).map((k) => k.headcount);
  const gm = kpi && kpi.revenue - kpi.nreRevenue > 0 ? (kpi.revenue - kpi.nreRevenue - kpi.cogs) / (kpi.revenue - kpi.nreRevenue) : 0;
  const score = view.dead ? 0 : view.finance.founderShare * (Math.max(0, view.cash) + 2 * ttm);

  const respinDecisions = view.projects.filter((p) => p.phase === "respinDecision");
  const tapeinReady = view.projects.filter((p) => p.phase === "waitingTapein");
  const tapeoutReady = view.projects.filter((p) => p.phase === "waitingTapeout" && view.nodes[p.nodeId]!.status === "qualified");
  const stalled = view.projects.filter((p) => p.prodQualStalled || p.respinFixStalled);
  const sheet = view.finance.openTermSheet;
  const pricing = view.ipo.process?.phase === "pricing" ? view.ipo.process : null;
  const nothingPending =
    respinDecisions.length === 0 &&
    tapeinReady.length === 0 &&
    tapeoutReady.length === 0 &&
    stalled.length === 0 &&
    !sheet &&
    !pricing &&
    !view.finance.activeRaise;

  return (
    <div>
      <h2>HQ — {monthLabel(view.monthIndex)}</h2>

      <div className="panel" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <CompanyIllustration stage={view.stage} headcount={kpi?.headcount ?? 7} />
        <div>
          <div className="title">{view.stage.toUpperCase()}</div>
          <span className="hint">the company, roughly to scale</span>
        </div>
      </div>

      <div className="row">
        <div className="tile">
          <div className="k">CASH</div>
          <div className={`v${view.cash < 500_000 ? " bad" : view.cash < 1_500_000 ? " warn" : ""}`}>{money(view.cash)}</div>
          <Sparkline data={cashSeries} />
        </div>
        <div className="tile">
          <div className="k">REVENUE / MO (incl. NRE)</div>
          <div className="v">{money(kpi?.revenue ?? 0)}</div>
          <Sparkline data={revSeries} />
        </div>
        <div className="tile">
          <div className="k">HEADCOUNT</div>
          <div className="v">{kpi?.headcount ?? 7}</div>
          <Sparkline data={headSeries} />
        </div>
      </div>

      <div className="row">
        <div className="tile">
          <div className="k">TTM PRODUCT REVENUE</div>
          <div className="v">{money(ttm)}</div>
          <div className="sub">gross margin {kpi && kpi.revenue - kpi.nreRevenue > 0 ? pct(gm) : "—"}</div>
        </div>
        <div className="tile">
          <div className="k">MARKET SHARE (Σ)</div>
          <div className="v">{pct(kpi?.share ?? 0)}</div>
          <div className="sub">{view.products.filter((p) => p.active).length} active products</div>
        </div>
        <div className="tile">
          <div className="k">FOUNDER SCORE (if it ended today)</div>
          <div className="v">{money(score)}</div>
          <div className="sub">
            {pct(view.finance.founderShare, 0)} of cash + 2×TTM · {view.stage.toUpperCase()} stage
          </div>
        </div>
      </div>

      {view.asks.length > 0 && (
        <>
          <h3>ON YOUR DESK ({view.asks.length})</h3>
          {view.asks.map((ask) => {
            const queued = pending.find((c) => c.type === "answerAsk" && c.askId === ask.id);
            const queuedOption = queued && queued.type === "answerAsk" ? queued.option : null;
            return (
              <div className="panel" key={ask.id}>
                <div className="title">
                  {ask.headline.toUpperCase()} <span className="badge amber">{ask.source.toUpperCase()}</span>
                  <span className="hint">
                    {" "}
                    · {ask.monthsLeft} month{ask.monthsLeft === 1 ? "" : "s"} to answer — silence means “{ask.options[ask.defaultOption]!.label.toLowerCase()}”
                  </span>
                </div>
                <p>{ask.body}</p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {ask.options.map((opt, i) => (
                    <button
                      key={i}
                      className={`act${queuedOption === i ? " queued warn" : ""}`}
                      onClick={() => session.queue({ type: "answerAsk", askId: ask.id, option: i })}
                    >
                      {queuedOption === i ? "◆ " : ""}
                      {opt.label.toUpperCase()}
                    </button>
                  ))}
                </div>
                {ask.footnote && <div className="hist-note">{ask.footnote}</div>}
              </div>
            );
          })}
        </>
      )}

      <h3>PENDING DECISIONS</h3>
      <div className="panel">
        {nothingPending && <span className="hint">Nothing demands a decision. End the turn, or invest the month.</span>}
        {sheet && (
          <div className="pending-list">
            <div className="p">
              <span className="warn-text">
                ◆ TERM SHEET: {money(sheet.amount)} at {money(sheet.preMoney)} pre{sheet.downRound ? " — DOWN ROUND" : ""} · expires in{" "}
                {sheet.monthsLeft} month{sheet.monthsLeft === 1 ? "" : "s"}
              </span>
              <button className="act warn" onClick={() => go("boardroom")}>
                DECIDE ▸
              </button>
            </div>
          </div>
        )}
        {pricing && (
          <div className="pending-list">
            <div className="p">
              <span className="warn-text">
                ◆ PRICING NIGHT: the book supports ${pricing.rangeLo?.toFixed(2)}–${pricing.rangeHi?.toFixed(2)} — price the IPO
              </span>
              <button className="act warn" onClick={() => go("street")}>
                DECIDE ▸
              </button>
            </div>
          </div>
        )}
        {respinDecisions.map((p) => (
          <div key={p.id} className="pending-list">
            <div className="p">
              <span className="bad-text">
                ◆ {p.id}: first silicon DEAD — respin (½ mask cost) or cancel?
              </span>
              <button className="act bad" onClick={() => go("pipeline")}>
                DECIDE ▸
              </button>
            </div>
          </div>
        ))}
        {tapeinReady.map((p) => (
          <div key={p.id} className="pending-list">
            <div className="p">
              <span className="warn-text">
                ◆ {p.id}: design complete — coverage {pct(p.coverage)} · tape in to freeze the netlist (low coverage risks a return to design)
              </span>
              <button className="act warn" onClick={() => go("pipeline")}>
                REVIEW ▸
              </button>
            </div>
          </div>
        ))}
        {tapeoutReady.map((p) => (
          <div key={p.id} className="pending-list">
            <div className="p">
              <span className="warn-text">
                ◆ {p.id}: layout complete — coverage {pct(p.coverage)} · tape out when confident
              </span>
              <button className="act warn" onClick={() => go("pipeline")}>
                REVIEW ▸
              </button>
            </div>
          </div>
        ))}
        {stalled.map((p) => (
          <div key={p.id} className="pending-list">
            <div className="p">
              <span className="warn-text">◆ {p.id}: {p.prodQualStalled ? "production qual stalled (needs 2 implementation engineers)" : "respin fix stalled (no designers)"}</span>
              <button className="act warn" onClick={() => go("pipeline")}>
                STAFF ▸
              </button>
            </div>
          </div>
        ))}
        {view.finance.activeRaise && (
          <div className="hint">
            ◆ Round open: asking {money(view.finance.activeRaise.ask)}, month {view.finance.activeRaise.monthsActive + 1} of meetings…
          </div>
        )}
      </div>

      <h3>ORDERS QUEUED THIS MONTH ({pending.length})</h3>
      <div className="panel pending-list">
        {pending.length === 0 && <span className="hint">No orders yet. They execute when you end the turn.</span>}
        {pending.map((c, i) => (
          <div key={i} className="p">
            <span>{describeCommand(c)}</span>
            <button onClick={() => session.unqueue(i)} title="cancel order">
              ✗
            </button>
          </div>
        ))}
      </div>

      {view.path === null && (
        <div className="panel">
          <div className="title">THE FORK</div>
          <span className="hint">
            Your first chip locks the company's path: <b>digital</b> ({content.markets.find((m) => m.pathAffinity === "digital")?.displayName}) or{" "}
            <b>analog</b> ({content.markets.find((m) => m.pathAffinity === "analog")?.displayName}). Off-path chips later cost{" "}
            {((content.economy.models.offPathEffortMult - 1) * 100).toFixed(0)}% more effort and read requirements worse. Choose with your
            first project on the PIPELINE screen.
          </span>
        </div>
      )}
    </div>
  );
}

function describeCommand(c: { type: string } & Record<string, unknown>): string {
  switch (c.type) {
    case "hire":
      return `hire ${c.count} ${c.discipline}`;
    case "fire":
      return `lay off ${c.count} ${c.discipline}`;
    case "setNre":
      return `assign ${c.engineers} designers to NRE consulting`;
    case "startNodeQual":
      return `start qualification: ${c.nodeId}`;
    case "setYieldResearch":
      return `yield research on ${c.nodeId}: ${c.engineers} engineers`;
    case "setReliability":
      return `reliability program on ${c.nodeId}: ${c.engineers} engineers`;
    case "startProject":
      return `start project in ${c.marketId} on ${c.nodeId} (${c.kGates} kGates)`;
    case "setStaffing": {
      const s = c.staffing as Record<string, number>;
      return `staff ${c.projectId}: D${s.design}/V${s.verification}/I${s.implementation}`;
    }
    case "tapeIn":
      return `TAPE IN ${c.projectId} (freeze the netlist)`;
    case "tapeOut":
      return `TAPE OUT ${c.projectId}`;
    case "respin":
      return `respin ${c.projectId}`;
    case "cancelProject":
      return `cancel ${c.projectId}`;
    case "endOfLifeProduct":
      return `end-of-life ${c.productId}`;
    case "raiseRound":
      return `open a round: ask ${money(c.ask as number)}`;
    case "acceptTermSheet":
      return "ACCEPT the term sheet";
    case "rejectTermSheet":
      return "reject the term sheet";
    case "fileS1":
      return `file the S-1 (${c.underwriter} underwriter)`;
    case "priceIpo":
      return `price the IPO at $${(c.perShare as number).toFixed(2)}`;
    case "setGuidance":
      return `guidance policy: ${(c.policy as number) > 0 ? "stretch" : "sandbag"} ${Math.abs((c.policy as number) * 100).toFixed(0)}%`;
    case "secondaryOffering":
      return `secondary offering: ${money(c.amount as number)}`;
    case "buyback":
      return `buy back ${money(c.amount as number)} of stock`;
    case "setChannelPolicy":
      return `channel policy ${c.marketId}: ${(c.months as number).toFixed(1)} months of stock`;
    case "answerAsk":
      return `decision: answer ${c.askId} (option ${(c.option as number) + 1})`;
    case "setCompPolicy": {
      const p = c.policy as number;
      return `comp policy: ${p > 0 ? "above" : p < 0 ? "below" : "at"} market${p !== 0 ? ` (${Math.abs(p * 100).toFixed(0)}%)` : ""}`;
    }
    case "setEdaTier":
      return `EDA tier: ${c.tier}`;
    case "setToolStaff":
      return `in-house ${c.discipline} tool team: ${c.engineers} engineers`;
    case "acquireRival":
      return `ACQUIRE ${c.rivalId} (${c.payment})`;
    default:
      return c.type;
  }
}
